import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Icon, Button, NewButton, Modal, EditableTitle } from '../../../../../components';

import { AssetData } from '../AssetData';

import {
  spaceShape,
  questionShape,
  sourceShape,
  termTranslationShape
} from '../../../../../propsShape';

import { VersionsHistory } from '../../molecules';

const AssetModalHeader = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--border-muted);
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const AssetModalFooter = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: 12px;
  border-top: 1px solid var(--border-muted);
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const AssetModalContainer = styled.div`
  display: flex;
  flex: 1;
`;

const AssetModalBody = styled.div`
  padding: 4px;
  margin-bottom: 20px;
  flex: 1;
  overflow: auto;
`;

const AssetModal = ({
  question,
  spaces,
  sources,
  termTranslations,
  navigate,
  onStartChat,
  onCreateSpace,
  onAssetTitleChange,
  onAddQuestionToSpace,
  onRemoveQuestionFromSpace,
  handleColumnOrderChange,
  retryQuestion,
  onClose = null,
  isReadOnly = false,
  fetchNextPage = null,
  hasNextPage = false
}) => {
  const [title, setTitle] = useState(question.assetTitle || question.body);
  const [selectedVersion, setSelectedVersion] = useState(question);
  const [activeView, setActiveView] = useState('table');

  useEffect(() => {
    // Reset selectedVersion when the main question changes (different asset)
    // or when the current question's answers change (like during retry operations)
    // or when the status changes (pipeline steps during retry)
    setSelectedVersion(question);
  }, [question.id, question.answers?.length, question.answers?.[0]?.id, question.status]); // React to asset changes, answer changes, and status changes

  // Log whenever the question prop changes
  useEffect(() => {
    // Empty useEffect - could be removed if not needed
  }, [question]);

  const handleAssetTitleChange = useCallback(
    (newTitle) => {
      onAssetTitleChange(question, newTitle);
      setTitle(newTitle);
    },
    [question]
  );

  const handleRetryQuestion = useCallback(
    (questionId) => {
      retryQuestion(questionId);
    },
    [retryQuestion]
  );

  return (
    <Modal size='lg' bodyStyle={{ padding: 0 }}>
      {(close) => (
        <>
          <AssetModalHeader>
            <EditableTitle
              title={title}
              onTitleChange={handleAssetTitleChange}
              isReadOnly={isReadOnly}
            />
            <NewButton
              tertiary
              onClick={() => {
                if (onClose) {
                  onClose();
                }
                close();
              }}
            >
              <Icon name='close' />
            </NewButton>
          </AssetModalHeader>
          <AssetModalContainer>
            {question?.versions?.length > 0 && (
              <VersionsHistory
                versions={question.versions}
                selectedVersion={selectedVersion}
                setSelectedVersion={setSelectedVersion}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
              />
            )}
            <AssetModalBody>
              <AssetData
                isAssetModalFailedRefreshAttempt={selectedVersion?.answer?.isCanned}
                isEditable={false}
                question={selectedVersion}
                spaces={spaces}
                sources={sources}
                termTranslations={termTranslations}
                navigate={navigate}
                handleRetryQuestion={handleRetryQuestion}
                initialActiveView={selectedVersion.lastChartFollowUp ? 'chart' : 'table'}
                isReadOnly={isReadOnly}
                activeView={activeView}
                onColumnOrderChange={handleColumnOrderChange}
                onCreateSpace={onCreateSpace}
                onAddQuestionToSpace={onAddQuestionToSpace}
                onRemoveQuestionFromSpace={onRemoveQuestionFromSpace}
                handleViewChange={setActiveView}
              />
            </AssetModalBody>
          </AssetModalContainer>
          <AssetModalFooter>
            <Button
              tertiary
              onClick={() => {
                if (onClose) {
                  onClose();
                }
                close();
              }}
            >
              Close
            </Button>
            <Button
              primary
              onClick={() => {
                onStartChat();
                close();
              }}
            >
              <Icon name='qa' size={24} />
              Start chat
            </Button>
          </AssetModalFooter>
        </>
      )}
    </Modal>
  );
};

AssetModal.propTypes = {
  onClose: PropTypes.func,
  onStartChat: PropTypes.func.isRequired,
  onAssetTitleChange: PropTypes.func.isRequired,
  onAddQuestionToSpace: PropTypes.func.isRequired,
  onCreateSpace: PropTypes.func.isRequired,
  onRemoveQuestionFromSpace: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  question: questionShape.isRequired,
  spaces: PropTypes.arrayOf(spaceShape).isRequired,
  sources: PropTypes.arrayOf(sourceShape).isRequired,
  termTranslations: PropTypes.arrayOf(termTranslationShape).isRequired,
  handleColumnOrderChange: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  fetchNextPage: PropTypes.func,
  hasNextPage: PropTypes.bool,
  retryQuestion: PropTypes.func.isRequired
};

export { AssetModal };
